/**
 * Overview Tab Component for Meeting Detail
 * Displays meeting information, attendees, and processing status
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Mic, FileText, Wand2, CheckCircle, Users,
  Building2, Video
} from 'lucide-react';

const OverviewTab = ({
  meeting,
  recording,
  tasks,
  minutes
}) => {
  const getMeetingTypeIcon = () => {
    switch (meeting.meeting_type) {
      case 'teams': return <Video className="h-5 w-5 text-purple-600" />;
      case 'hybrid': return <Users className="h-5 w-5 text-amber-600" />;
      default: return <Building2 className="h-5 w-5 text-blue-600" />;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Meeting Info */}
      <Card className="lg:col-span-2" data-testid="meeting-info-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {getMeetingTypeIcon()}
            Meeting Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {meeting.description && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">Description</h4>
              <p className="text-foreground">{meeting.description}</p>
            </div>
          )}
          
          {meeting.agenda && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">Agenda</h4>
              <pre className="text-sm text-foreground whitespace-pre-wrap bg-muted p-3 rounded-lg">
                {meeting.agenda}
              </pre>
            </div>
          )}
          
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Attendees</h4>
            <div className="flex flex-wrap gap-2">
              {meeting.attendees?.length > 0 ? (
                meeting.attendees.map((attendee, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                      attendee.is_internal === false 
                        ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' 
                        : 'bg-muted'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      attendee.is_internal === false
                        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'
                    }`}>
                      {attendee.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{attendee.name}</p>
                      <p className="text-xs text-muted-foreground">{attendee.email}</p>
                      {attendee.is_internal === false && (
                        <span className="text-xs text-amber-600">External</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No attendees added</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Processing Status */}
      <Card data-testid="processing-status-card">
        <CardHeader>
          <CardTitle>Processing Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
            <div className="flex items-center gap-2">
              <Mic className={`h-5 w-5 ${recording?.status === 'completed' ? 'text-emerald-500' : 'text-muted-foreground'}`} />
              <span className="text-sm">Recording</span>
            </div>
            <Badge variant={recording?.status === 'completed' ? 'default' : 'outline'}>
              {recording?.status || 'Not recorded'}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
            <div className="flex items-center gap-2">
              <FileText className={`h-5 w-5 ${meeting.transcription_status === 'completed' ? 'text-emerald-500' : 'text-muted-foreground'}`} />
              <span className="text-sm">Transcription</span>
            </div>
            <Badge variant={meeting.transcription_status === 'completed' ? 'default' : 'outline'}>
              {meeting.transcription_status || 'Not started'}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
            <div className="flex items-center gap-2">
              <Wand2 className={`h-5 w-5 ${tasks.length > 0 ? 'text-emerald-500' : 'text-muted-foreground'}`} />
              <span className="text-sm">AI Analysis</span>
            </div>
            <Badge variant={tasks.length > 0 ? 'default' : 'outline'}>
              {tasks.length > 0 ? 'Completed' : 'Not started'}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
            <div className="flex items-center gap-2">
              <CheckCircle className={`h-5 w-5 ${minutes?.approval_status === 'approved' ? 'text-emerald-500' : 'text-muted-foreground'}`} />
              <span className="text-sm">Minutes</span>
            </div>
            <Badge variant={minutes?.approval_status === 'approved' ? 'default' : 'outline'}>
              {minutes?.approval_status || 'Not generated'}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
